import { ComponentProps } from 'react'
import { z } from 'zod'

import Label from '@/components/Label'
import Pill from '@/components/TailwindUI/Pill'
import { Popover } from '@/components/TailwindUI/Popover'

// KEEP IN SYNC WITH:
// https://github.com/Workgrid/aws-workgrid-saas-unified-bot-platform/blob/main/src/lambda/ask/api/constants/candidateLabels.js#L12
const FEATURE_CATEGORIES = [
  'Information Technology',
  'HR & Talent',
  'Office & Facility',
  'Finance & Procurement',
  'Legal & Contracts',
  'Benefits',
  'Travel & Expense',
  'Productivity',
  'Sales & CRM',
  'Training & Learning'
]
export const categorySchema = z.object({
  name: z.string()
})
export type Category = z.infer<typeof categorySchema>

type CategoriesProps = {
  name: string
  value: Array<Category>
  disabled?: boolean
  label?: string
  tooltip?: string
  onAdd: (category: Category) => void
  onRemove: (index: number) => void
  heading?: string
  subHeading?: string
}

const Categories = ({
  name,
  value,
  onAdd,
  onRemove,
  disabled = false,
  label,
  tooltip,
  heading,
  subHeading,
  ...rest
}: ComponentProps<'div'> & CategoriesProps) => {
  const updateSelectedCategories = (category: string) => {
    if (disabled) return
    const existingIndex = value.findIndex(c => c.name === category)
    if (existingIndex === -1) {
      onAdd({
        name: category
      })
    } else {
      onRemove(existingIndex)
    }
  }

  return (
    <div className="mt-5" {...rest}>
      {label ? (
        <div className="flex">
          {disabled ? (
            <Label className="block text-sm font-semibold leading-6 text-zinc-600">{label}</Label>
          ) : (
            <Label>{label}</Label>
          )}
          {tooltip && <Popover as="div" tooltipMessage={tooltip} />}
        </div>
      ) : (
        <div>
          <h3 className="font-semibold">{heading}</h3>
          <div className="flex">
            <p className="mt-1 text-sm font-semibold text-zinc-500">{subHeading}</p>
            {tooltip && <Popover as="div" tooltipMessage={tooltip} />}
          </div>
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-3">
        {FEATURE_CATEGORIES.map(category => {
          const isSelected = value.some(c => c.name === category)
          return (
            <Pill
              key={category}
              selected={isSelected}
              label={category}
              onClick={() => updateSelectedCategories(category)}
              disabled={disabled}
            />
          )
        })}
      </div>
    </div>
  )
}
export default Categories
